// Import des hooks React
import { useState, useEffect, useCallback } from "react";
// Axios pour les appels API
import axios from "axios";
// Composants Material UI
import { Button, IconButton, Stack, Snackbar, Alert } from "@mui/material";
// Icônes Material UI
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
// Hook MUI pour détecter si on est sur mobile
import { useMediaQuery, Box } from "@mui/material";

import { useLanguage } from "../context/LanguageContext.js";
import { useGroupe } from "../context/GroupeContext.js";

import { useAdminRole } from "../hooks/useAdminRole.js";

import { logDev } from "../utils/logger.js";
import { useSnackbar } from "../hooks/useSnackbar.js";

const InfosMap = () => {
  const { t } = useLanguage();
  // Groupe courant
  const { groupeId } = useGroupe();
  // Rôle de l’utilisateur
  const role = useAdminRole(groupeId);
  const isAdmin = role === "admin";

  // ⚠️ Préparation pour le futur multi‑images 
  const [images, setImages] = useState([]);

  const { snackbar, setSnackbar, showSnackbar } = useSnackbar();
  
  logDev("[InfosMap] rendu", { groupeId, role, isAdmin });

  // Détection mobile
  const isMobile = useMediaQuery("(max-width:600px)");

  // États locaux
  // États locaux pour l’image unique de cette page
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // États pour le lien externe
  const [link, setLink] = useState("");
  const [savedLink, setSavedLink] = useState("");

  // --- Gestion image ---
  // Lorsqu’un fichier est choisi
  const handleImageChange = useCallback((e) => {
     if (!isAdmin) return showSnackbar({ message: t("ActionReservedForAdmin"), severity: "error" });
    const file = e.target.files?.[0];
    if (!file) return;
    logDev("[InfosMap] image choisie", { name: file.name, size: file.size });
    setImageFile(file);

    // Création d’une URL temporaire pour l’aperçu
    setImagePreview(URL.createObjectURL(file));
  }, [isAdmin, t, showSnackbar]);

  // --------------------------------------------------------- 
  // 🗑️ GESTION IMAGE — Suppression de l’image affichée 
  // ---------------------------------------------------------
  const handleDeleteImage = useCallback(async () => {
    if (!isAdmin) return showSnackbar({ message: t("ActionReservedForAdmin"), severity: "error" });
    if (!groupeId) return;

    // 🔥 Sécurité obligatoire + vérification du type
if (!imagePreview || typeof imagePreview !== "string" || !imagePreview.includes("/")) {
  console.warn("[InfosMap] Pas d'image valide à supprimer", imagePreview);
  return showSnackbar({ message: t("NoImageToDelete"), severity: "warning" });
}


    // Extraction du nom du fichier depuis l’URL publique
    const fileName = imagePreview.split("/").pop();

    logDev("[InfosMap] suppression image", { groupeId });

    try {
      await axios.delete(`${process.env.REACT_APP_API}/infosmap/delete-image/${groupeId}/${fileName}`);
      
      // Reset local
      setImagePreview(null);
      setImageFile(null);

      showSnackbar({ message: t("ImageDeleted"), severity: "success" });
    } catch (err) {
      logDev("❌ Erreur suppression image", { message: err.message, stack: err.stack });
      showSnackbar({ message: t("ErrorDeletingImage"), severity: "error" });
    }
 }, [groupeId, isAdmin, t, showSnackbar, imagePreview]);

  // --------------------------------------------------------- 
  // 📤 GESTION IMAGE — Upload vers le backend 
  // ---------------------------------------------------------
  const handleUploadImage = useCallback(async () => {
    if (!isAdmin) return showSnackbar({ message: t("ActionReservedForAdmin"), severity: "error" });
    if (!imageFile || !groupeId) return showSnackbar({ message: t("MissingImageOrGroup"), severity: "warning" });

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("groupeId", groupeId);

    logDev("[InfosMap] upload image", { groupeId, file: imageFile?.name });

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API}/infosmap/upload-image`, formData);
      // On construit l’URL publique de l’image
      //const imageUrl = `${process.env.REACT_APP_API}/${data.imageMap}`;
      //setImagePreview(imageUrl);

      // Le backend renvoie l’URL publique → on l’affiche
      setImagePreview(data.imageMap);

      showSnackbar({ message: t("ImageSavedForGroup"), severity: "success" });
    } catch (err) {
      logDev("❌ Erreur upload image", { message: err.message, stack: err.stack });
      showSnackbar({ message: t("ErrorSavingImage"), severity: "error" });
    }
  }, [imageFile, groupeId, isAdmin, t, showSnackbar]);

  // --------------------------------------------------------- 
  // 🔗 GESTION LIEN — Suppression 
  // ---------------------------------------------------------
  const handleDeleteLink = useCallback(async () => {
    if (!isAdmin) return showSnackbar({ message: t("ActionReservedForAdmin"), severity: "error" });
    if (!groupeId) return;

    logDev("[InfosMap] suppression lien", { groupeId });

    try {
      await axios.delete(`${process.env.REACT_APP_API}/groupes/${groupeId}/link`);

      setLink("");
      setSavedLink("");

      showSnackbar({ message: t("LinkDeletedSuccess"), severity: "success" });
    } catch (err) {
      logDev("❌ Erreur suppression lien", {
        message: err.message,
        response: err.response?.data,
        stack: err.stack
      });
      showSnackbar({ message: t("ErrorDeletingLink"), severity: "error" });
    }
 }, [groupeId, isAdmin, t, showSnackbar]);

  // --------------------------------------------------------- 
  // 🔗 GESTION LIEN — Sauvegarde 
  // ---------------------------------------------------------
  const handleSaveLink = useCallback(async () => {
    if (!isAdmin) return showSnackbar({ message: t("ActionReservedForAdmin"), severity: "error" });
    
    const lienFinal = link.trim();
    setSavedLink(lienFinal);
    
    if (!groupeId || !lienFinal) return;

    logDev("[InfosMap] sauvegarde lien", { groupeId, lien: lienFinal });

    try {
      await axios.post(`${process.env.REACT_APP_API}/groupes/${groupeId}/link`, { lien: lienFinal });
      
      showSnackbar({ message: t("LinkSavedSuccess"), severity: "success" });
    } catch (err) {
      logDev("❌ Erreur sauvegarde lien", { message: err.message, stack: err.stack });
      showSnackbar({ message: t("ErrorSavingLink"), severity: "error" });
    }
  }, [groupeId, link, isAdmin, t, showSnackbar]);

  // --------------------------------------------------------- 
  // 📥 CHARGEMENT INITIAL — Récupération image + lien 
  // ---------------------------------------------------------
  useEffect(() => {
    if (!groupeId) return;

    (async () => {
      try {
        // Récupération des infos du groupe
        const { data } = await axios.get(`${process.env.REACT_APP_API}/groupes/${groupeId}`);
        logDev("[InfosMap] données groupe reçues", data);
        /*if (data.imageMap) {
          const baseUrl = process.env.REACT_APP_API.replace("/api", "");
          setImagePreview(`${baseUrl}/${data.imageMap}`);
        }*/

        // On affiche la première image du tableau (mode "une seule image")
        //if (data.imagesMap?.length > 0) { 
        //  setImagePreview(data.imagesMap[0]); 
        //}

        // Compatibilité ancien + nouveau backend
        const img =
          data?.imagesMap?.[0] ||   // nouveau format
          data?.imageMap ||         // ancien format
          null;

        setImagePreview(img);
        
        // Chargement du lien
        if (data.lienMap) {
          setLink(data.lienMap);
          setSavedLink(data.lienMap);
        }
      } catch (err) {
        logDev("❌ Erreur récupération image/lien du groupe", { message: err.message, stack: err.stack });
      }
    })();

    // Nettoyage de l’URL blob local si utilisée
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [groupeId]);

  return (
    <div 
    style={{ 
      backgroundColor: "#f4ece2", 
      padding: "10px", 
      borderRadius: "8px", 
      width: "100%", 
      boxSizing: "border-box" 
      }}
      >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {!imagePreview && !savedLink && (
  <Box
    sx={{
      textAlign: "center",
      padding: isMobile ? 2 : 4,
      backgroundColor: "#e4dcd2",
      borderRadius: 2,
      marginBottom: 2,
      color: "#555",
      fontSize: isMobile ? "0.9rem" : "1rem",
      fontStyle: "italic",
      maxWidth: isMobile ? "90%" : "auto",
      margin: "0 auto",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }}
  >
    {isAdmin
      ? `🗺️ ${t("EmptyInfosMapAdmin")}`
      : `🗺️ ${t("EmptyInfosMapUser")}`}
  </Box>
)}

      {/* Image upload */}
      {(isAdmin || imagePreview) && (
      <div className="section" style={{ textAlign: "center" }}>
        {isAdmin && (
        <label style={{ display: "block", marginBottom: "8px" }}>
          📷 {t("UploadAnImage")} :
        </label>
        )}

        {isAdmin ? (
          <>
            <Button
              variant="contained"
              color="primary"
              component="label"
              startIcon={<SaveIcon />}
              disabled={!!imagePreview}
            >
              {t("ChooseFile")}
              <input type="file" accept="image/*" hidden onChange={handleImageChange} />
            </Button>

            {imagePreview ? (
              <>
                <p style={{ marginTop: "12px" }}>
                  🛑 {t("AnImageIsAlreadySavedForThisGroup")}<br />
                  {t("DeleteItSoYouCanUploadANewOne")}
                </p>
                <img src={imagePreview} alt={t("ImagePreviewAlt")} style={{ marginTop: "12px", maxWidth: "100%", borderRadius: "6px" }} />

                <Stack direction="row" spacing={2} sx={{ mt: 2 }} justifyContent="center">
                  <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<SaveIcon />} 
                  onClick={handleUploadImage}
                  >
                    {!isMobile && t("SaveImage")}
                  </Button>
                  <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={<DeleteIcon />} 
                  onClick={handleDeleteImage}
                  >
                    {!isMobile && t("DeleteImage")}
                  </Button>
                </Stack>
              </>
            ) : (
              <p style={{ marginTop: "12px" }}>
                {t("NoImagesSavedForThisGroup")}
                </p>
            )}
          </>
        ) : (
          // Utilisateur non admin → juste l’aperçu
          imagePreview && (
            <img
              src={imagePreview}
              alt={t("ImagePreviewAlt")}
              style={{
                marginTop: "12px",
                maxWidth: "100%",
                borderRadius: "6px",
              }}
            />
          )
        )}
      </div>
    )}

      {/* Lien internet */}
      {(isAdmin || savedLink) && (
      <div className="linkInput" style={{ textAlign: "center" }}>
        {isAdmin && (
        <label htmlFor="linkInput">🔗 {t("Weblink")} :</label>
         )}

        {isAdmin && (
          <>
            <input
              type="text"
              id="linkInput"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://..."
              style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc", width: "60%", marginTop: "4px" }}
            />
            <Stack 
              direction="row" 
              justifyContent="center" 
              spacing={2} 
              sx={{ mt: 2 }}
            >
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<LinkIcon />} 
                onClick={handleSaveLink}
              >
                {!isMobile && t("Save")}
              </Button>
            </Stack>
          </>
        )}

        {savedLink && (
          <div style={{ marginTop: "12px" }}>
            <span>💡 {t("LinkSaved")} :</span>
            <div 
            style={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              gap: "8px" 
              }}
              >
                <a href={savedLink} target="_blank" rel="noopener noreferrer">
                  {savedLink}
                </a>
              {isAdmin && (
                <IconButton 
                  color="error" 
                  onClick={handleDeleteLink} 
                  title={t("RemoveLink"
                )}
                >
                  <DeleteIcon />
                </IconButton>
              )}

              

            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default InfosMap;
